import type { errorTypes } from 'c9-tracking';
import track from 'c9-tracking';
import type { RecoilState, RecoilValueReadOnly, Snapshot } from 'recoil';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { wizardStepAtom } from '../../recoil/atoms/wizardAtom';
import { pageIndex, pageName } from '../../config/wizardPagesConfig';
import { isLoggedOnSelector } from '../../recoil/selectors/isLoggedOnSelector';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';
import { isAxiosError } from 'axios';
import { checkoutProgressSelector } from '../../recoil/selectors/checkoutProgressSelector';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';

export const useErrorTracking = (): {
    trackFault: (message: string, errorType: errorTypes, error: Error | undefined) => void;
} => {
    const getValue = <T>(
        snapshot: Snapshot,
        recoilState: RecoilValueReadOnly<T> | RecoilState<T>
    ): T | undefined => {
        return snapshot.getLoadable(recoilState)?.valueMaybe();
    };
    const wizardStep = useRecoilValue(wizardStepAtom);
    const wizardStepName = pageName(wizardStep);

    const isLoggedOn = useRecoilValue(isLoggedOnSelector);

    const trackFault = useRecoilCallback(
        ({ snapshot }) =>
            (message: string, errorType: errorTypes, error?: Error): void => {
                // ONSA-related logs
                const onsaStepName =
                    wizardStep < pageIndex('ShoppingBasketPage')
                        ? getValue(snapshot, onsaProgressSelector)?.currentStepName
                        : undefined;

                const currentProduct =
                    wizardStep < pageIndex('ShoppingBasketPage')
                        ? getValue(snapshot, getOnsaCurrentProductSelector)
                        : undefined;

                // Checkout-related logs
                const checkoutProgress =
                    wizardStep === pageIndex('CheckoutPage')
                        ? getValue(snapshot, checkoutProgressSelector)
                        : undefined;

                const technicalInfo = [
                    `isLoggedOn: ${String(isLoggedOn)}`,
                    currentProduct ? `product: ${String(currentProduct.flavourName)}` : undefined,
                    `page: ${String(wizardStepName)}`,
                    onsaStepName && onsaStepName !== wizardStepName
                        ? `step: ${onsaStepName}`
                        : undefined,
                    checkoutProgress
                        ? `checkout step: ${String(checkoutProgress.currentStepName)}`
                        : undefined,
                    !!error &&
                    isAxiosError(error) &&
                    error.config?.headers?.['x-top-jsonrpc-method']
                        ? `failed PC call: ${String(error.config.headers['x-top-jsonrpc-method'])}`
                        : undefined,
                    !!error && isAxiosError(error) && error.config?.headers?.['X-TOP-TRACEID']
                        ? `traceId: ${String(error.config.headers['X-TOP-TRACEID'])}`
                        : undefined
                ]
                    .filter(Boolean)
                    .join(', ');

                void track.fault(message, errorType, message, technicalInfo);
            }
    );

    return { trackFault };
};
